import { supabase } from '@/integrations/supabase/client';

export type MomentoConferenciaInputFixa = 'importacao' | 'encaminhamento';
export type StatusConferenciaInputFixa = 'aprovado' | 'reprovado' | 'erro' | 'invalidada';

export interface ConferenciaInputFixa {
  id: string;
  momento: MomentoConferenciaInputFixa;
  status: StatusConferenciaInputFixa;
  contratos: string[];
  motivos: string[];
  criado_em: string;
  usuario_nome?: string | null;
}

export const COLUNA_INPUT_FIXA = 'INPUT REALIZADO FIXA';

async function chamar<T>(path: string, body?: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente antes de continuar.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  let r: Response;
  try {
    r = await fetch(`${base}/v1/input-fixa/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new Error('Não foi possível falar com o CRM para conferir os documentos de input. O pedido não andou.');
  }
  const result = await r.json().catch(() => ({ error: 'O CRM não respondeu à conferência dos documentos de input.' }));
  if (!r.ok) throw new Error(result.error || 'A conferência dos documentos de input falhou. O pedido não andou.');
  return result.data as T;
}

export function conferirInputFixa(pedidoId: string, momento: MomentoConferenciaInputFixa) {
  return chamar<ConferenciaInputFixa>(`${pedidoId}/conferir`, { momento });
}

export function listarConferenciasInputFixa(pedidoId: string) {
  return chamar<ConferenciaInputFixa[]>(pedidoId);
}

/** No envio o CRM julga de novo com a leitura já feita de cada print. Sem aprovação, o pedido de fixa não anda. */
export async function exigirInputFixaAprovado(pedidoId: string): Promise<void> {
  const c = await conferirInputFixa(pedidoId, 'encaminhamento');
  if (c.status === 'aprovado') return;
  const titulo = c.status === 'erro'
    ? 'A IA não conseguiu conferir os documentos do input agora.'
    : 'A IA reprovou os documentos do input.';
  throw new Error(`${titulo} ${c.motivos.join(' ')} Ajuste em Pedido 360 → Anexos → Documentos de Input Fixa.`);
}

const FAMILIA_FIXA = (familia: string | null | undefined) => {
  const f = (familia || '').toUpperCase();
  return f.includes('FIBRA') || f === 'FIXA BÁSICA';
};

/** Algum dos itens vai para Input realizado Fixa e tem input no Conexão? (fibra, TV, mesh: família de fixa).
 *  A TT Fixa fica fora: ela não tem input no Conexão, o input dela é o e-mail à gerente de contas
 *  e quem segura é o fn_pedido_inputado, com a resposta dela (Douglas 29/10/2026). */
export async function itensTemFixa(itensIds: string[]): Promise<boolean> {
  if (!itensIds.length) return false;
  const { data: itens, error } = await supabase
    .from('itens_pedido')
    .select('produto_id')
    .in('id', itensIds);
  if (error) throw error;
  const produtoIds = [...new Set((itens || []).map((i) => i.produto_id).filter((p): p is string => !!p))];
  if (!produtoIds.length) return false;
  const { data: produtos, error: errProdutos } = await supabase
    .from('produtos')
    .select('nome, familia')
    .in('id', produtoIds);
  if (errProdutos) throw errProdutos;
  return (produtos || []).some((p) => FAMILIA_FIXA(p.familia) && p.nome !== 'TT FIXA');
}
